import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Theme, applyTheme, getSystemTheme, parseTheme, readStoredTheme, storeTheme, watchSystemTheme } from '../utils/theme';

interface ThemeContextValue {
  theme: Theme;
  /** False until the stored theme has been read. */
  ready: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [theme, setTheme] = useState<Theme>(
    () => parseTheme(document.documentElement.getAttribute('data-theme')) ?? getSystemTheme(),
  );
  const [ready, setReady] = useState(false);
  const themeRef = useRef(theme);
  const storedRef = useRef<Theme | null>(null);
  const userChose = useRef(false);

  themeRef.current = theme;

  useEffect(() => {
    let active = true;
    void readStoredTheme().then((stored) => {
      if (!active) return;
      if (!userChose.current) {
        storedRef.current = stored;
        setTheme(stored ?? getSystemTheme());
      }
      setReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () =>
      watchSystemTheme((systemTheme) => {
        if (storedRef.current === null) setTheme(systemTheme);
      }),
    [],
  );

  // Applied while rendering, not in an effect: components that read CSS variables in render (charts) run before any effect of
  // this provider, and they must already see the new data-theme. The ref makes it run once per theme, also in StrictMode.
  const appliedRef = useRef<Theme | null>(null);
  if (appliedRef.current !== theme) {
    appliedRef.current = theme;
    applyTheme(theme);
  }

  const toggleTheme = useCallback(() => {
    const next: Theme = themeRef.current === 'dark' ? 'light' : 'dark';
    userChose.current = true;
    storedRef.current = next;
    setTheme(next);
    void storeTheme(next);
  }, []);

  const value = useMemo(() => ({ theme, ready, toggleTheme }), [theme, ready, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// ThemeProvider is the outermost app provider (App.tsx, renderWithProviders), so every component and hook has it.
export const useThemeContext = () => useContext(ThemeContext) as ThemeContextValue;
